'use client';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import InfiniteProductList from 'src/components/app/archive/components/products/infinite-list';

import handleGA4Event from '@/lib/service-workers';

import Container from '@/components/@base/container';
import Loading from '@/components/app/archive/components/products/loading';
import NoResult from '@/components/app/archive/components/products/no-result';
import RowProductCard from '@/components/app/archive/components/products/row-product-card';
import ThemeDemos from '@/components/app/archive/components/products/theme-demos';
import Props from '@/components/app/archive/components/products/type';
import { ARCHIVE_PAGE_TYPE, FILTER_KEYS } from '@/components/app/archive/helpers';
import { VIEW_MODE } from '@/components/app/archive/helpers/hooks/type';
import { useGetArchiveInfo, useGetProductList } from '@/components/app/archive/services/hooks';
import { PRODUCT_TYPE } from '@/components/app/product/components/@shared/type';
import { ProductCard } from '@/components/shared/components/cards';
import Demo from '@/components/shared/components/cards/demo';
import ReadySiteCard from '@/components/shared/components/cards/readySite';
import PaginationButtons from '@/components/shared/components/pagination-buttons';
import useScrollTo from '@/components/shared/helpers/hooks/use-scroll-to';

import { useViewModeStore } from '@/store/view-mode.store';

import { DATA_CY_PRODUCT_CARD, DATA_CY_PRODUCT_LIST_CARD } from '@/constant/cypress';
import ProductModel from '@/models/product.model';

export default function ProductList({
  slug = '',
  type,
  filters = {},
  page = 0,
  queryString = '',
}: Props) {
  const { viewMode } = useViewModeStore();
  const router = useRouter();
  const hasPageFilter = page > 0;
  const [selectedPage, setSelectedPage] = useState(hasPageFilter ? page : 1);

  const topPageRef = useRef<HTMLDivElement>(null);
  const scrollToNode = useScrollTo();
  const { data: archiveInfo } = useGetArchiveInfo(slug, type);

  const {
    isLoading: isPageLoading,
    data: selectedPageData,
    error: pageQueryError,
    refetch,
    isPending: isPagePending,
  } = useGetProductList(filters, selectedPage);

  useEffect(() => {
    if (hasPageFilter && selectedPage !== page) {
      const fetchPageData = async () => {
        const { data: newPage } = await refetch();
      };

      fetchPageData();
    }
  }, [selectedPage]);

  const totalPages = selectedPageData?.totalPages || 1;
  const pageBasePath =
    type === ARCHIVE_PAGE_TYPE.SEARCH ? `/search/page` : `/web/${type}/${slug}/page`;

  ////google tag manager
  useEffect(() => {
    if (archiveInfo && selectedPageData && selectedPageData?.data?.length > 0) {
      let viewItemListData = selectedPageData?.data?.map((item, index) => {
        return {
          item_id: item?.id,
          item_name: item?.title,
          price: item.price,
          quantity: 1,
          affiliation: item?.store?.slug,
          discount: item?.discount,
          index: index,
          item_list_name: 'archive',
          item_list_id: archiveInfo?.id,
        };
      });

      handleGA4Event('view_item_list', viewItemListData, {
        item_list_name: 'archive',
        item_list_id: archiveInfo?.id,
      });
    }
  }, [archiveInfo, selectedPageData]);
  //////////////

  if (pageQueryError) {
    throw pageQueryError;
  }

  const handlePageChange = (page) => {
    setSelectedPage(page);
    scrollToNode(topPageRef.current, 200);

    const newPageRoute =
      type === ARCHIVE_PAGE_TYPE.SEARCH
        ? `${pageBasePath}/${page}?${queryString}`
        : `${pageBasePath}/${page}?${queryString}`;
    router.push(newPageRoute, { scroll: false });
  };

  const renderProducts = (products: ProductModel[]) => {
    //pass a row af products and a row of demos to header
    const topRowProducts = products.slice(0, 3);
    const otherProducts = products.slice(3);
    const TopRow = () => (
      <Container>
        <Container className='mx-4 grid grid-cols-1 gap-4 pb-4 md:mx-0 md:grid-cols-2 lg:grid-cols-3'>
          {topRowProducts.map((product) =>
            product?.type === PRODUCT_TYPE.READY_SITE ? (
              <ReadySiteCard
                data={product}
                className='h-full! md:max-h-[410px]! md:min-h-[390px]!'
              />
            ) : (
              <ProductCard
                className='mx-auto border-[1px]! border-solid! border-[#E5E8EB]!'
                data={product}
                data-cy={DATA_CY_PRODUCT_CARD}
              />
            ),
          )}
        </Container>
      </Container>
    );

    if (filters[FILTER_KEYS.VIEW_DEMO] === true) {
      return (
        <Container className='mx-4 grid grid-cols-1 gap-4 pb-4 md:m-0 md:grid-cols-2 lg:grid-cols-3'>
          {products.map((product) => (
            <Demo data={product} />
          ))}
        </Container>
      );
    }

    if (viewMode === VIEW_MODE.LIST) {
      return (
        <Container className='hidden md:block'>
          {products?.map((product) => (
            <Container className='py-2'>
              <RowProductCard data-cy={DATA_CY_PRODUCT_LIST_CARD} {...product} />
            </Container>
          ))}
        </Container>
      );
    }

    return (
      <>
        <TopRow />
        {type !== ARCHIVE_PAGE_TYPE.SEARCH && <ThemeDemos slug={slug} type={type} />}
        <Container className='mx-4 grid grid-cols-1 gap-4 pb-4 md:mx-0 md:grid-cols-2 lg:grid-cols-3'>
          {otherProducts.map((product) =>
            product?.type === PRODUCT_TYPE.READY_SITE ? (
              <ReadySiteCard
                data={product}
                className='h-full! md:max-h-[410px]! md:min-h-[390px]!'
              />
            ) : (
              <ProductCard
                className='mx-auto border-[1px]! border-solid! border-[#E5E8EB]! md:mx-0'
                data={product}
                isLoading={isPageLoading}
                data-cy={DATA_CY_PRODUCT_CARD}
              />
            ),
          )}
        </Container>
      </>
    );
  };

  const noResult = selectedPageData?.data.length === 0;

  return (
    <>
      <Container ref={topPageRef} />
      <Container className='hidden w-full group-has-data-pending/pending:block'>
        <Loading numberOfCards={6} />
      </Container>

      <Container className='block w-full group-has-data-pending/pending:hidden'>
        {isPageLoading && <Loading numberOfCards={6} />}

        {noResult ? <NoResult /> : selectedPageData && renderProducts(selectedPageData.data)}

        {!hasPageFilter && totalPages > 1 && (
          <InfiniteProductList
            viewMode={viewMode}
            filters={filters}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            pagePath={pageBasePath}
          />
        )}

        {hasPageFilter && totalPages && !noResult && (
          <PaginationButtons
            totalPages={totalPages}
            currentPage={selectedPage}
            pagePath={pageBasePath}
            onPageChange={handlePageChange}
          />
        )}
      </Container>
    </>
  );
}
